import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpLeft } from "lucide-react";
import Footer from "@/components/athena/Footer";

const SECTIONS = [
  {
    heading: "1. Acceptance of these terms",
    body: (
      <>
        These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access
        to and use of the website
        <span className="whitespace-nowrap"> athenanutrascience.com</span>
        {" "}(the &ldquo;Site&rdquo;), operated by Athena Nutrascience Pvt.
        Ltd. (&ldquo;Athena&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;),
        registered in India at Mumbai&nbsp;400&nbsp;063. By accessing, browsing
        or interacting with the Site, you confirm that you have read,
        understood and agree to be bound by these Terms. If you do not agree,
        please do not use the Site.
      </>
    ),
  },
  {
    heading: "2. Nature of the Site",
    body: (
      <>
        The Site is an informational and B2B marketing platform describing
        Athena&apos;s work across nutraceuticals, biotechnology and life
        sciences. It is not a storefront: no products can be purchased through
        the Site, and no medical, dietary or clinical advice is offered
        through it. All product references (including codes such as
        ATH-101) describe research and development pipeline items and are
        intended for informational purposes for prospective partners,
        clinicians and researchers.
      </>
    ),
  },
  {
    heading: "3. Not medical advice",
    body: (
      <>
        Content on the Site — including descriptions of formulations, bio-
        actives, clinical programmes and research outcomes — is provided for
        general informational purposes only. It does <strong>not</strong>{" "}
        constitute medical, nutritional, pharmaceutical or professional
        advice. You must not rely on any statement on the Site as a
        substitute for consultation with a qualified healthcare or
        regulatory professional. Athena disclaims all liability for any
        action taken based on content of the Site.
      </>
    ),
  },
  {
    heading: "4. Intellectual property",
    body: (
      <>
        The Site, its content, design, source code, brand name (&ldquo;Athena
        Nutrascience&rdquo;), product names, trademarks (including
        &ldquo;Metaboline®&rdquo;, &ldquo;Neurelix™&rdquo;), logos, imagery,
        typography selections and copy are owned by or licensed to Athena and
        are protected by copyright, trademark and other intellectual-property
        laws. You may view and print pages of the Site for your personal,
        non-commercial reference only. You may not copy, reproduce,
        republish, upload, post, transmit, distribute, or exploit any content
        for commercial purposes without our prior written consent.
      </>
    ),
  },
  {
    heading: "5. Permitted use",
    body: (
      <>
        You agree not to:
        <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[#B89B64]">
          <li>Use the Site in any unlawful, fraudulent or harmful manner.</li>
          <li>Submit false, misleading or defamatory information through the contact form.</li>
          <li>Attempt to gain unauthorised access to any part of the Site, its servers, or connected systems.</li>
          <li>Introduce malware, viruses, or any technology intended to damage the Site or its users.</li>
          <li>Scrape, harvest, or systematically extract content from the Site by automated means.</li>
          <li>Frame or mirror the Site without our written permission.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "6. Enquiries submitted through the contact form",
    body: (
      <>
        When you submit an enquiry through our contact form, you warrant that
        the information you provide is accurate and that you are authorised
        to share it. We may retain your submission to respond to your enquiry
        and for our internal records, as described in our{" "}
        <Link to="/privacy" className="link-underline text-[#1A382A] font-medium">
          Privacy Policy
        </Link>
        . We are under no obligation to respond to unsolicited proposals or
        commercial offers.
      </>
    ),
  },
  {
    heading: "7. Third-party links",
    body: (
      <>
        The Site may contain links to third-party websites or resources for
        your convenience (for example, social profiles or research
        publications). Such links do not constitute an endorsement by
        Athena, and we are not responsible for the content, availability, or
        privacy practices of any third-party site.
      </>
    ),
  },
  {
    heading: "8. Disclaimers",
    body: (
      <>
        The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo; basis. To the fullest extent permitted by law,
        Athena disclaims all warranties, express or implied, including but
        not limited to warranties of merchantability, fitness for a
        particular purpose, non-infringement, uninterrupted availability, and
        error-free operation. Content may be updated or corrected without
        notice, and we make no guarantee that information is complete or
        current.
      </>
    ),
  },
  {
    heading: "9. Limitation of liability",
    body: (
      <>
        To the fullest extent permitted by applicable law, Athena, its
        founders, employees, agents and partners shall not be liable for any
        indirect, incidental, consequential, special, or punitive damages
        arising from or related to your access to or use of (or inability to
        use) the Site — including loss of profits, data, goodwill or business
        opportunity — even if we have been advised of the possibility of such
        damages.
      </>
    ),
  },
  {
    heading: "10. Indemnity",
    body: (
      <>
        You agree to indemnify and hold harmless Athena, its founders,
        employees and partners from any claim, loss, liability, damage or
        expense (including reasonable legal fees) arising out of your breach
        of these Terms or your misuse of the Site.
      </>
    ),
  },
  {
    heading: "11. Modifications to the Site & Terms",
    body: (
      <>
        We may change, suspend, or discontinue any aspect of the Site at any
        time without notice. We may also revise these Terms from time to
        time; the revised Terms take effect the moment they are posted to
        this page, and your continued use of the Site after that date
        constitutes acceptance.
      </>
    ),
  },
  {
    heading: "12. Governing law & jurisdiction",
    body: (
      <>
        These Terms are governed by and construed in accordance with the
        laws of the Republic of India, without regard to conflict-of-law
        principles. Any dispute arising out of or relating to these Terms or
        your use of the Site shall be subject to the exclusive jurisdiction
        of the competent courts in Mumbai, Maharashtra, India.
      </>
    ),
  },
  {
    heading: "13. Contact",
    body: (
      <>
        Questions about these Terms may be sent to{" "}
        <a
          href="mailto:connect@athenanutrascience.com"
          className="link-underline text-[#1A382A] font-medium"
        >
          connect@athenanutrascience.com
        </a>
        .
      </>
    ),
  },
];

export default function Terms() {
  useEffect(() => {
    document.title = "Terms & Conditions — Athena Nutrascience";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-[#F8F7F4] min-h-screen">
      {/* Simple top bar */}
      <header className="border-b border-[#D5D1C8] bg-[#F8F7F4]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="terms-nav-home"
            className="flex items-center gap-3 group"
          >
            <span className="w-8 h-8 rounded-full border border-[#1A382A] flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[#1A382A] group-hover:bg-[#B89B64] transition-colors" />
            </span>
            <span className="font-display text-2xl tracking-tight text-[#0A1713] leading-none">
              Athena<span className="text-[#B89B64]">.</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="flex items-center gap-2 label-mono hover:text-[#1A382A] transition-colors"
          >
            <ArrowUpLeft size={14} strokeWidth={1.5} /> BACK TO HOME
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <span className="label-mono">// LEGAL · TERMS &amp; CONDITIONS</span>
        <h1
          data-testid="terms-title"
          className="font-display text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight text-[#0A1713]"
        >
          The rules of
          <br />
          the <em className="italic text-[#1A382A]">engagement</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-[#3D4F46] leading-relaxed">
          Athena Nutrascience is a B2B research and development company. This
          page explains the terms under which you may access and use our
          website, and the mutual obligations that apply when you interact
          with us.
        </p>
        <div className="mt-8 label-mono text-[#3D4F46]">
          Last updated: December 2025
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          {/* Sticky mini-TOC */}
          <aside className="lg:col-span-1 lg:sticky lg:top-32 lg:self-start">
            <div className="label-mono mb-6">/ ON THIS PAGE</div>
            <ol className="space-y-2 text-sm">
              {SECTIONS.map((s) => {
                const id = s.heading.split(" ")[0].replace(".", "");
                return (
                  <li key={s.heading}>
                    <a
                      href={`#t${id}`}
                      className="text-[#3D4F46] hover:text-[#1A382A] transition-colors block"
                    >
                      {s.heading}
                    </a>
                  </li>
                );
              })}
            </ol>
          </aside>

          <article className="lg:col-span-2 space-y-12">
            {SECTIONS.map((s) => {
              const id = s.heading.split(" ")[0].replace(".", "");
              return (
                <div
                  key={s.heading}
                  id={`t${id}`}
                  className="scroll-mt-32 border-t border-[#D5D1C8] pt-8 first:border-t-0 first:pt-0"
                >
                  <h2 className="font-display text-2xl md:text-3xl text-[#0A1713] leading-tight">
                    {s.heading}
                  </h2>
                  <div className="mt-4 text-[#3D4F46] leading-relaxed text-base">
                    {s.body}
                  </div>
                </div>
              );
            })}
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
